import { Component, EventEmitter, Input, OnInit, Output, OnDestroy, OnChanges, SimpleChanges, ViewChild, ViewChildren, QueryList, AfterViewInit } from '@angular/core';

import { NgbAccordionModule } from '@ng-bootstrap/ng-bootstrap';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { SelectFieldComponent } from '../../../../components/form/select-field/select-field.component';
import { ProjectsTeamComponent } from '../projects-team/projects-team.component';
import { ProjectsWorkingListComponent } from '../projects-working-list/projects-working-list.component';
import { ProjectsRtpComponent } from '../projects-rtp/projects-rtp.component';
import { ProjectsCertificateComponent } from '../projects-certificate/projects-certificate.component';
import { ProjectsContractsDocsComponent } from '../projects-contracts-docs/projects-contracts-docs.component';
import { ProjectsSummaryComponent } from '../projects-summary/projects-summary.component';
import { ProjectsService } from 'src/app/services/api/projects.service';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ProjectsDocumentsRequestedToClientComponent } from '../projects-documents-requested-to-client/projects-documents-requested-to-client.component';
import { ProjectsDocumentsComponent } from '../projects-documents/projects-documents.component';
import { ProjectsListOfItemsToTestComponent } from '../projects-list-of-items-to-test/projects-list-of-items-to-test.component';
import { ProjectsChecklistComponent } from '../projects-checklist/projects-checklist.component';
import { ProjectsTestlistComponent } from '../projects-testlist/projects-testlist.component';
import { ProjectsBatchComponent } from '../projects-batch/projects-batch.component';
import { ProjectsIssueComponent } from '../projects-issue/projects-issue.component';
import { OptionService } from 'src/app/services/api/option.service';
import { MainLayoutService } from 'src/app/services/main-layout.service';
import { AccessControlService } from 'src/app/services/access-control.service';
import { FeatureActionKey } from 'src/app/layouts/main-layout/components/sidebar/config/access-control.config';
import { Location } from '@angular/common';
import { ActivatedRoute } from '@angular/router';
import { RouteService } from 'src/app/services/route.service';
import { ProjectsFinanceComponent, ProjectsFinanceView } from '../projects-finance/projects-finance.component';
import { ProjectsFinanceAuthorizationComponent } from '../projects-finance-authorization/projects-finance-authorization.component';

export interface SubmittableComponent {
  submitForm(): void;
}

@Component({
  selector: 'projects-accordion',
  imports: [
    NgbAccordionModule,
    ReactiveFormsModule,
    SelectFieldComponent,
    ProjectsTeamComponent,
    ProjectsWorkingListComponent,
    ProjectsRtpComponent,
    ProjectsCertificateComponent,
    ProjectsContractsDocsComponent,
    ProjectsSummaryComponent,
    ProjectsDocumentsRequestedToClientComponent,
    ProjectsDocumentsComponent,
    ProjectsListOfItemsToTestComponent,
    ProjectsChecklistComponent,
    ProjectsTestlistComponent,
    ProjectsBatchComponent,
    ProjectsIssueComponent,
    ProjectsFinanceComponent,
    ProjectsFinanceAuthorizationComponent
],
  templateUrl: './projects-accordion.component.html',
  styleUrl: './projects-accordion.component.scss'
})
export class ProjectsAccordionComponent implements OnInit, OnChanges, OnDestroy, AfterViewInit {
  @Input() projectId?: string;
  @Input() project?: any;
  @Input() readonly: boolean = false;
  @Output() onFormDataUpdated = new EventEmitter<any>();
  @Output() onSubmitRequested = new EventEmitter<void>();

  @ViewChild(ProjectsTeamComponent) projectsTeamComponent?: ProjectsTeamComponent;
  @ViewChild(ProjectsWorkingListComponent) projectsWorkingListComponent?: ProjectsWorkingListComponent;
  @ViewChild(ProjectsRtpComponent) projectsRtpComponent?: ProjectsRtpComponent;
  @ViewChild(ProjectsCertificateComponent) projectsCertificateComponent?: ProjectsCertificateComponent;
  @ViewChild(ProjectsContractsDocsComponent) projectsContractsDocsComponent?: ProjectsContractsDocsComponent;
  @ViewChild(ProjectsSummaryComponent) projectsSummaryComponent?: ProjectsSummaryComponent;
  @ViewChild(ProjectsDocumentsRequestedToClientComponent) projectsDocumentsRequestedToClientComponent?: ProjectsDocumentsRequestedToClientComponent;
  @ViewChild(ProjectsDocumentsComponent) projectsDocumentsComponent?: ProjectsDocumentsComponent;
  @ViewChild(ProjectsListOfItemsToTestComponent) projectsListOfItemsToTestComponent?: ProjectsListOfItemsToTestComponent;
  @ViewChild(ProjectsChecklistComponent) projectsChecklistComponent?: ProjectsChecklistComponent;
  @ViewChild(ProjectsTestlistComponent) projectsTestlistComponent?: ProjectsTestlistComponent;
  @ViewChild(ProjectsIssueComponent) projectsIssueComponent?: ProjectsIssueComponent;
  @ViewChild(ProjectsBatchComponent) projectsBatchComponent?: ProjectsBatchComponent;

  jurisdictionFilterControl: FormControl = new FormControl('');
  
  activeTabId: string = 'summary';
  
  loadedTabs: Set<string> = new Set(['summary']);

  readonly TABS_WITHOUT_SAVE_BUTTON: string[] = ['contractsDocs', 'projectDocument', 'finance'];

  private readonly TEMPORARILY_DISABLED_TABS: string[] = ['rtp'];

  private readonly GATED_TAB_FEATURE_KEYS: Partial<Record<string, FeatureActionKey>> = {
    finance: 'PROJECTS_FINANCE'
  };

  financeInitialView: ProjectsFinanceView = 'income';

  private readonly CUSTOMER_TAB_FEATURE_KEYS: Partial<Record<string, FeatureActionKey>> = {
    summary: 'PROJECTS_TAB_SUMMARY',
    documentsRequestedToClient: 'PROJECTS_TAB_DOCUMENTS_REQUESTED_TO_CLIENT',
    team: 'PROJECTS_TAB_TEAM',
    testList: 'PROJECTS_TAB_TESTLIST',
    issues: 'PROJECTS_TAB_ISSUES',
    certificate: 'PROJECTS_TAB_CERTIFICATE'
  };

  private activeProfileCode: string | null = null;

  testlistCanEdit: boolean = true;

  private destroy$ = new Subject<void>();

  jurisdictionsOptions: { key: string, value: string }[] = [];

  constructor(
    private projectsService: ProjectsService,
    private optionsService: OptionService,
    private mainLayoutService: MainLayoutService,
    private accessControlService: AccessControlService,
    private route: ActivatedRoute,
    private location: Location,
    private routeService: RouteService
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['project']) {
      // The change of scope badge is shown next to the jurisdiction filter, not in the layout header
      this.mainLayoutService.setIsScopeChangePage(false);
    }
  }

  ngOnInit(): void {
    this.loadJurisdictions();

    this.mainLayoutService.activeProfile$
      .pipe(takeUntil(this.destroy$))
      .subscribe(profileCode => {
        this.activeProfileCode = profileCode;
      });

    const routeTab = this.route.snapshot.data['projectTab'] as string | undefined;
    if (routeTab) {
      this.financeInitialView = (this.route.snapshot.data['financeView'] as ProjectsFinanceView | undefined) ?? 'income';
      this.activateTab(routeTab, false);
    }

    this.jurisdictionFilterControl.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(value => {
        this.reloadActiveChildData(value);
      });
  }

  get hasChangeOfScope(): boolean {
    return !!this.project?.hasProposalChangeOfScope;
  }

  ngAfterViewInit(): void {
  }

  ngOnDestroy(): void {
    this.mainLayoutService.setIsScopeChangePage(false);
    this.destroy$.next();
    this.destroy$.complete();
  }

  submitActiveChildForm(): void {
    const activeComponent = this.getActiveChildComponent();
    if (activeComponent && this.hasSubmitMethod(activeComponent)) {
      activeComponent.submitForm();
    } else {
      console.warn(`No submittable component found for active tab: ${this.activeTabId}`);
    }
  }

  private getActiveChildComponent(): any {
    switch (this.activeTabId) {
      case 'team':
        return this.projectsTeamComponent;
      case 'workingList':
        return this.projectsWorkingListComponent;
      case 'rtp':
        return this.projectsRtpComponent;
      case 'certificate':
        return this.projectsCertificateComponent;
      case 'contractsDocs':
        return this.projectsContractsDocsComponent;
      case 'summary':
        return this.projectsSummaryComponent;
      case 'documentsRequestedToClient':
        return this.projectsDocumentsRequestedToClientComponent;
      case 'projectDocument':
        return this.projectsDocumentsComponent;
      case 'listItemsToTest':
        return this.projectsListOfItemsToTestComponent;
      case 'checkList':
        return this.projectsChecklistComponent;
      case 'testList':
        return this.projectsTestlistComponent;
      case 'batches':
        return this.projectsBatchComponent;
      case 'issues':
        return this.projectsIssueComponent;
      default:
        return null;
    }
  }

  private hasSubmitMethod(component: any): component is SubmittableComponent {
    return component && typeof component.submitForm === 'function';
  }

  
  loadJurisdictions(): void {
      this.optionsService.projectGlobalJurisdictions(this.projectId).subscribe((jurisdictions: any) => {
        const mappedOptions = jurisdictions.data.options.map((jurisdiction: any) => ({ 
          key: jurisdiction.key, 
          value: jurisdiction.value 
        }));
        
        this.jurisdictionsOptions = [
          { key: '', value: ' ' },
          ...mappedOptions
        ];
      });
  }
  
  onTeamDataUpdated(data: any): void {
    this.onFormDataUpdated.emit({
      type: 'team',
      data
    });
  }
  
  onWorkingListDataUpdated(event: any): void {
    this.onFormDataUpdated.emit(event);
  }
  
  onRtpDataUpdated(event: any): void {
    this.onFormDataUpdated.emit(event);
  }
  
  onCertificateDataUpdated(event: any): void {
    this.onFormDataUpdated.emit(event);
  }
  
  onContractsDocsDataUpdated(event: any): void {
    this.onFormDataUpdated.emit(event);
  }
  
  onSummaryDataUpdated(event: any): void {
    this.onFormDataUpdated.emit(event);
  }

  onDocumentsRequestedDataUpdated(event: any): void {
    this.onFormDataUpdated.emit(event);
  }

  onProjectDocumentDataUpdated(event: any): void {
    this.onFormDataUpdated.emit(event);
  }

  onTestItemsDataUpdated(event: any): void {
    this.onFormDataUpdated.emit(event);
  }

  onTestlistDataUpdated(event: any): void {
    this.onFormDataUpdated.emit({
      type: 'testlist',
      data: event
    });
  }

  onBatchDataUpdated(event: any): void {
    this.onFormDataUpdated.emit({
      type: 'batch',
      data: event
    });
  }

  tabs = [
    { id: 'summary', title: 'Summary', active: true }, 
    { id: 'documentsRequestedToClient', title: 'Client Documents', active: false },
    { id: 'projectDocument', title: 'Project Document', active: false },   
    { id: 'team', title: 'Team', active: false },
    { id: 'listItemsToTest', title: 'Items to Test', active: false },   
    { id: 'workingList', title: 'Working List', active: false },
    { id: 'checkList', title: 'Check List', active: false },
    { id: 'batches', title: 'Batches', active: false },
    { id: 'testList', title: 'Test List', active: false },
    { id: 'issues', title: 'Issues', active: false },
    { id: 'summaryReport', title: 'Summary Report', active: false },
    { id: 'finance', title: 'Finance', active: false },
    { id: 'certificate', title: 'Report', active: false },
    { id: 'contractsDocs', title: 'Contracts & Docs', active: false },
    { id: 'rtp', title: 'Rtp', active: false },
  ];

  
  activateTab(selectedTabId: string, syncUrl: boolean = true): void {
    if (syncUrl && selectedTabId === this.activeTabId) return;

    if (syncUrl) {
      this.financeInitialView = 'income';
      this.syncFinanceUrl(selectedTabId);
    }

    this.tabs.forEach(tab => {
      tab.active = tab.id === selectedTabId;
    });
    
    this.activeTabId = selectedTabId;
    
    this.loadedTabs.add(selectedTabId);
  }
  
  isTabActive(tabId: string): boolean {
    return this.activeTabId === tabId;
  }

  private hasRestrictedTabAccess(): boolean {
    return this.accessControlService.hasFeatureActionAccess(
      this.activeProfileCode, 'PROJECTS_TAB_SUMMARY', 'view'
    ) || this.accessControlService.hasFeatureActionAccess(
      this.activeProfileCode, 'PROJECTS_TAB_TEAM', 'view'
    );
  }

  isTabVisible(tabId: string): boolean {
    if (this.TEMPORARILY_DISABLED_TABS.includes(tabId)) return false;
    const gatedKey = this.GATED_TAB_FEATURE_KEYS[tabId];
    if (gatedKey && !this.accessControlService.hasFeatureActionAccess(this.activeProfileCode, gatedKey, 'view')) return false;
    if (!this.hasRestrictedTabAccess()) return true;
    const key = this.CUSTOMER_TAB_FEATURE_KEYS[tabId];
    if (!key) return false;
    return this.accessControlService.hasFeatureActionAccess(this.activeProfileCode, key, 'view');
  }

  private syncFinanceUrl(selectedTabId: string): void {
    if (!this.readonly || !this.projectId) return;
    const projectsDetailUrl = `/projects/${this.projectId}`;
    const targetUrl = selectedTabId === 'finance'
      ? this.routeService.projectsFinanceIncomeUrl(Number(this.projectId))
      : projectsDetailUrl;
    if (this.location.path() !== targetUrl) {
      this.location.replaceState(targetUrl);
    }
  }

  onTestlistCanEditChange(canEdit: boolean): void {
    this.testlistCanEdit = canEdit;
  }

  isSaveButtonVisible(): boolean {
    if (this.hasRestrictedTabAccess()) return false;
    if (this.TABS_WITHOUT_SAVE_BUTTON.includes(this.activeTabId)) return false;
    if (this.activeTabId === 'testList' && !this.testlistCanEdit) return false;
    return true;
  }

  private reloadActiveChildData(jurisdictionId: string | number | null): void {
    switch (this.activeTabId) {
      case 'listItemsToTest':
        this.projectsListOfItemsToTestComponent?.reloadWithJurisdiction(jurisdictionId);
        break;
      case 'workingList':
        this.projectsWorkingListComponent?.reloadWithJurisdiction(jurisdictionId);
        break;
      case 'checkList':
        this.projectsChecklistComponent?.reloadWithJurisdiction(jurisdictionId);
        break;
      case 'testList':
        this.projectsTestlistComponent?.reloadWithJurisdiction(jurisdictionId);
        break;
      default:
        break;
    }
  }
}
